// client/src/components/AffiliatesDocs.js

import React, { useEffect, useState, useMemo } from 'react';
import '../CSS/Dashboard.css';
import AffiliatesNavbar from './AffiliatesNavbar';
import { db, storage } from '../../firebase';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, query, limit } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { FaLock, FaExternalLinkAlt, FaUsers, FaFileImage } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// =========================
// Constantes docs
// =========================
const CONFIDENTIAL_FILES = [
  'ARGUMENTATIONS.png',
  'INSTALLATION CLIENT.png',
  'PROBLEME CLIENT.png',
];

const PUBLIC_FILES_SINGLE = [
  'ENONCER ECOBILL.png',
  'PROCESS EN CAISSE.png',
  'CREER ACCES CAISSE.png',
  'CREER PUBLICATION.png',
  'CREER ACCES NOTES DE FRAIS.png',
];

const PUBLIC_GROUPS = [
  {
    name: 'FLYER 1 (RECTO + VERSO).png',
    paths: [
      'Administratif/FLYER 1 RECTO.png',
      'Administratif/FLYER 1 VERSO.png',
    ],
  },
];

// =========================
// Cache session (URLs + gate)
// =========================
const URL_CACHE_PREFIX = 'affdocs_url_by_path_v1:'; // + storagePath => { ts, url }
const URL_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const PROMO_GATE_CACHE_KEY = 'affdocs_promo_gate_v1'; // { ts, hasPromo }
const PROMO_GATE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

const getSessionJSON = (key, def = null) => {
  try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; }
};
const setSessionJSON = (key, val) => {
  try { sessionStorage.setItem(key, JSON.stringify(val)); } catch {}
};


const readUrlCache = (path) => {
  const key = URL_CACHE_PREFIX + path;
  const item = getSessionJSON(key);
  if (!item) return null;
  if (Date.now() - (item.ts || 0) > URL_CACHE_TTL_MS) return null;
  return item.url || null;
};
const writeUrlCache = (path, url) => {
  const key = URL_CACHE_PREFIX + path;
  setSessionJSON(key, { ts: Date.now(), url: url || null });
};

const readPromoGateCache = () => {
  const item = getSessionJSON(PROMO_GATE_CACHE_KEY);
  if (!item) return null;
  if (Date.now() - (item.ts || 0) > PROMO_GATE_TTL_MS) return null;
  return typeof item.hasPromo === 'boolean' ? item.hasPromo : null;
};
const writePromoGateCache = (hasPromo) => {
  setSessionJSON(PROMO_GATE_CACHE_KEY, { ts: Date.now(), hasPromo: !!hasPromo });
};

// =========================
// Détection hard reload / ?fresh=1
// =========================
const isHardReload = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('fresh') === '1') return true;
  } catch {}
  try {
    const navs = performance.getEntriesByType && performance.getEntriesByType('navigation');
    if (navs && navs[0]) return navs[0].type === 'reload';
    // legacy
    // eslint-disable-next-line
    if (performance && performance.navigation) return performance.navigation.type === 1;
  } catch {}
  return false;
};

const AffiliatesDocs = () => {
  // ---- contrôle activation via codes promo ----
  // eslint-disable-next-line
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingPromoGate, setLoadingPromoGate] = useState(true);
  const [hasPromo, setHasPromo] = useState(false);

  // ---- données docs (inchangé côté UI) ----
  const [confidentialDocs, setConfidentialDocs] = useState([]);
  const [publicDocs, setPublicDocs] = useState([]);
  const navigate = useNavigate();

  const mapSingles = useMemo(
    () => (names, scope) =>
      names.map((name) => ({
        type: 'single',
        name,
        path: `Administratif/${name}`,
        scope, // 'confidential' | 'public'
        url: null,
        error: null,
      })),
    []
  );

  const mapGroups = useMemo(
    () => (groups, scope) =>
      groups.map((g) => ({
        type: 'group',
        name: g.name,
        paths: g.paths, // array of storage paths
        scope,
        error: null,
      })),
    []
  );

  // =========================
  // Helpers Storage (URL) — cache + forceFresh
  // =========================
  const getFileURL = async (storagePath, { forceFresh = false } = {}) => {
    if (!forceFresh) {
      const cached = readUrlCache(storagePath);
      if (cached !== null) return cached; // peut être null si échec précédent mis en cache
    }
    try {
      const url = await getDownloadURL(ref(storage, storagePath));
      writeUrlCache(storagePath, url);
      return url;
    } catch (e) {
      // on met en cache null pour éviter boucles coûteuses pendant 24h
      writeUrlCache(storagePath, null);
      throw e;
    }
  };

  // =========================
  // Auth + gate codes promo (économique)
  // =========================
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user || null);

      if (!user) {
        setHasPromo(false);
        setLoadingPromoGate(false);
        return;
      }

      // Force la récupération des custom claims à jour : si le rôle a été
      // mis à jour côté admin (validation affilié, ajout/retrait régie), le
      // token courant peut encore avoir l'ancien claim → 403 sur Administratif/.
      try { await user.getIdToken(true); } catch {}

      const forceRefresh = isHardReload(); // ?fresh=1 ou F5

      // 0) Tenter cache gate (si pas force)
      if (!forceRefresh) {
        const cached = readPromoGateCache();
        if (cached !== null) {
          setHasPromo(!!cached);
          setLoadingPromoGate(false);
          return;
        }
      }

      try {
        // 1) Lire doc parent uniquement
        const promoDocRef = doc(db, 'code promo', user.uid);
        const promoDocSnap = await getDoc(promoDocRef);

        let found = false;

        if (promoDocSnap.exists()) {
          const data = promoDocSnap.data() || {};
          // a) Champ promoCode directement au doc parent
          if (data.promoCode && String(data.promoCode).trim() !== '') {
            found = true;
          } else {
            // b) Sinon, vérifier existence d'au moins 1 code avec limit(1) (pas de scan complet)
            const codesColRef = collection(promoDocRef, 'codes');
            const q1 = query(codesColRef, limit(1));
            const snap1 = await getDocs(q1);
            if (!snap1.empty) found = true;
          }
        }

        setHasPromo(found);
        writePromoGateCache(found); // cache 6h
      } catch (e) {
        console.error('Erreur contrôle codes promo :', e);
        // prudence → pas d’accès
        setHasPromo(false);
        // on ne met pas en cache un échec (pour ne pas bloquer)
      } finally {
        setLoadingPromoGate(false);
      }
    });

    return () => unsub();
  }, []);

  // =========================
  // Chargement des docs (uniquement si hasPromo)
  // =========================
  useEffect(() => {
    if (!hasPromo) return; // ne charge pas les docs tant que pas activé

    const forceFresh = isHardReload(); // en cas de ?fresh=1 ou F5, on bypasse le cache URL

    const loadSingles = async (items, setter) => {
      const loaded = await Promise.all(
        items.map(async (it) => {
          try {
            const url = await getFileURL(it.path, { forceFresh });
            return { ...it, url };
          } catch (e) {
            return { ...it, error: e?.message || 'Erreur inconnue' };
          }
        })
      );
      setter(loaded);
    };

    // Confidentiel : uniquement des singles
    loadSingles(mapSingles(CONFIDENTIAL_FILES, 'confidential'), setConfidentialDocs);

    // Public : singles + groups (les groups n'ont pas besoin d'URL ici)
    (async () => {
      const singleDefs = mapSingles(PUBLIC_FILES_SINGLE, 'public');
      const singles = await Promise.all(
        singleDefs.map(async (it) => {
          try {
            const url = await getFileURL(it.path, { forceFresh });
            return { ...it, url };
          } catch (e) {
            return { ...it, error: e?.message || 'Erreur inconnue' };
          }
        })
      );
      const groups = mapGroups(PUBLIC_GROUPS, 'public');
      setPublicDocs([...groups, ...singles]);
    })();
  }, [hasPromo, mapSingles, mapGroups]);

  // =========================
  // Navigation viewer
  // =========================
  const goToViewer = (docItem) => {
    if (!docItem) return;

    const payload =
      docItem?.type === 'group'
        ? { name: docItem.name, scope: docItem.scope || 'public', paths: docItem.paths }
        : { name: docItem.name, scope: docItem.scope || 'public', paths: [docItem.path] };

    try {
      sessionStorage.setItem('affdocs_view', JSON.stringify(payload));
    } catch {}

    navigate('/affiliatesdocsviewer', { state: payload });
  };

  // =============== RENDER ===============
  // Libellé lisible (affichage uniquement) : "INSTALLATION CLIENT.png" → "Installation client"
  const prettyName = (name = '') => {
    const base = String(name).replace(/\.(png|jpe?g|pdf|webp)$/i, '').trim().toLowerCase();
    return base.charAt(0).toUpperCase() + base.slice(1);
  };

  const DOCS_CSS = `
    .afd-wrap { width: 100%; max-width: var(--eb-content-max); margin: 0 auto; }
    .afd-wrap .eb-page-title { margin: 0; text-align: left; }
    .afd-state { padding: 40px 24px; display: flex; flex-direction: column; align-items: center; gap: 14px; text-align: center; color: var(--eb-muted); font-size: 14.5px; width: 100%; }
    .afd-section { padding: 0; overflow: hidden; }
    .afd-section + .afd-section { margin-top: 24px; }
    .afd-section-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .afd-section-title { margin: 0; font-size: 15px; font-weight: 650; color: var(--eb-text); text-align: left; }
    .afd-list { padding: 0 20px; }
    .afd-card { flex-wrap: nowrap; transition: background .15s ease; }
    .afd-doc { display: flex; align-items: center; gap: 12px; min-width: 0; }
    @media (max-width: 600px) {
      .afd-list { padding: 0 14px; }
      .afd-card { flex-wrap: nowrap !important; }
    }
    .afd-icon { width: 40px; height: 40px; border-radius: var(--eb-radius-sm); background: var(--eb-brand-50); color: var(--eb-brand); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .afd-icon--conf { background: var(--eb-danger-bg); color: var(--eb-danger); }
    .afd-name { font-weight: 600; font-size: 15px; color: var(--eb-text); word-break: break-word; }
    .afd-meta { font-size: 13px; color: var(--eb-muted); display: flex; align-items: center; gap: 6px; margin-top: 2px; }
    .afd-err { color: var(--eb-danger); font-size: 13px; font-weight: 500; margin-top: 2px; }
  `;

  // Gate d’attente pendant la vérif des codes
  if (loadingPromoGate) {
    return (
      <div>
        <AffiliatesNavbar />
        <div className="dashboard-container">
          <style>{DOCS_CSS}</style>
          <div className="afd-wrap">
            <div className="eb-card afd-state">
              <span className="eb-spinner" aria-hidden="true" />
              Chargement…
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si pas de code promo → compte non activé
  if (!hasPromo) {
    return (
      <div>
        <AffiliatesNavbar />
        <div className="dashboard-container">
          <style>{DOCS_CSS}</style>
          <div className="afd-wrap">
            <header className="eb-page-header">
              <div>
                <h1 className="eb-page-title">Docs & ressources</h1>
                <p className="eb-page-subtitle">Supports de vente et documents à partager avec vos clients.</p>
              </div>
            </header>
            <div className="eb-card afd-state">
              <span className="afd-icon"><FaLock aria-hidden="true" /></span>
              <strong style={{ color: 'var(--eb-text)', fontWeight: 600 }}>En attente de validation du compte</strong>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderDoc = (d, loadingVisible) => (
    <div className="eb-row afd-card" key={d.name}>
      <div className="afd-doc eb-row__main">
        <span className={`afd-icon${d.scope === 'confidential' ? ' afd-icon--conf' : ''}`}>
          {d.scope === 'confidential' ? <FaLock aria-hidden="true" /> : <FaFileImage aria-hidden="true" />}
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="afd-name" title={d.name}>{prettyName(d.name)}</div>
          {d.error && <div className="afd-err">Erreur: {d.error}</div>}
          {loadingVisible && (
            <div className="afd-meta"><span className="eb-spinner eb-spinner--sm" aria-hidden="true" style={{ width: 12, height: 12 }} /> Chargement…</div>
          )}
        </div>
      </div>
      <div className="eb-row__actions">
        <button
          className="eb-btn eb-btn--secondary eb-btn--sm"
          style={{ minHeight: 40 }}
          onClick={() => goToViewer(d)}
          disabled={d.type === 'single' ? !d.url : false}
          title="Ouvrir dans une page dédiée"
        >
          <FaExternalLinkAlt aria-hidden="true" /> Ouvrir
        </button>
      </div>
    </div>
  );

  // Sinon, on affiche le contenu normal
  return (
    <div>
      <AffiliatesNavbar />
      <div className="dashboard-container">
        <style>{DOCS_CSS}</style>

        <div className="afd-wrap">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title">Docs & ressources</h1>
              <p className="eb-page-subtitle">Supports de vente et documents à partager avec vos clients.</p>
            </div>
          </header>

          {/* Section Confidentiel */}
          <section className="eb-card afd-section">
            <div className="eb-card__head">
              <div className="afd-section-head">
                <h2 className="afd-section-title">Confidentiel</h2>
                <span className="eb-badge eb-badge--danger"><FaLock aria-hidden="true" /> Usage interne</span>
              </div>
            </div>
            <div className="afd-list">
              {confidentialDocs.map((d) => renderDoc(d, !d.url && !d.error))}
            </div>
          </section>

          {/* Section À partager avec les clients */}
          <section className="eb-card afd-section">
            <div className="eb-card__head">
              <div className="afd-section-head">
                <h2 className="afd-section-title">À partager avec les clients</h2>
                <span className="eb-badge"><FaUsers aria-hidden="true" /> Public</span>
              </div>
            </div>
            <div className="afd-list">
              {publicDocs.map((d) => renderDoc(d, d.type === 'single' && !d.url && !d.error))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AffiliatesDocs;
